/**
 * Trabajos facturados que ya se tendrían que haber cobrado.
 *
 * ## La regla, y por qué no es «todo lo que falta cobrar»
 *
 * En el campo se factura a fin de mes y se cobra el mes siguiente. Un trabajo
 * de octubre se factura el 31 y entra en noviembre: que el 22 de octubre
 * figure sin cobrar no significa nada, es el curso normal de las cosas.
 *
 * Por eso el aviso **no mira cuánto falta cobrar, sino desde cuándo**. Un
 * trabajo está atrasado cuando su mes de facturación quedó **dos meses o más**
 * atrás: mirado desde octubre, atrasado es agosto para atrás. Septiembre se
 * está cobrando ahora mismo y no es deuda de nadie.
 *
 * El corte es de Manuel, no una convención: así le pagan a él.
 *
 * ## Por qué importa que sea exigente
 *
 * Un aviso que marca todo lo que no se cobró dice «tenés tres millones
 * pendientes» todos los días del año, incluido el día que facturaste. Eso se
 * aprende a ignorar en dos semanas y después ya no sirve ni cuando hay un
 * problema de verdad.
 *
 * Avisar de lo que de verdad se pasó —y sólo de eso— es lo que hace que el día
 * que aparece un cartel, se lo mire.
 */
import type { WorkListRow } from '@/features/works/works';

/** Meses de gracia: el mes en curso y el anterior, que se está cobrando. */
const MESES_DE_GRACIA = 2;

/** Un período como número comparable, para no comparar año y mes por separado. */
const comoNumero = (year: number, month: number) => year * 12 + (month - 1);

/**
 * ¿El período de facturación de este trabajo ya se pasó de su mes de cobro?
 *
 * `hoy` entra por parámetro y no se lee de un reloj acá adentro: una función
 * que mira la hora del sistema no se puede probar, y esta decide si alguien es
 * deudor.
 */
export function estaAtrasado(
  billingYear: number,
  billingMonth: number,
  hoy: Date = new Date(),
): boolean {
  const limite = comoNumero(hoy.getFullYear(), hoy.getMonth() + 1) - MESES_DE_GRACIA;
  return comoNumero(billingYear, billingMonth) <= limite;
}

export interface Atraso {
  /** Cuántos trabajos. */
  cantidad: number;
  /** Cuánto suman. `null` si alguno no tiene precio: no se inventa un total. */
  total_cents: number | null;
  /** El período más viejo con algo sin cobrar, para poder nombrarlo. */
  masViejo: { year: number; month: number } | null;
  /** Cuántos clientes distintos deben, que es lo que dice a cuántos hay que llamar. */
  clientes: number;
}

export const SIN_ATRASO: Atraso = {
  cantidad: 0,
  total_cents: 0,
  masViejo: null,
  clientes: 0,
};

/**
 * Resume lo atrasado a partir de los trabajos facturados y no cobrados.
 *
 * Recibe la lista ya filtrada por la consulta (`PENDIENTE_DE_COBRAR`): acá sólo
 * se aplica el corte por antigüedad y se suma.
 */
export function resumirAtrasos(works: readonly WorkListRow[], hoy: Date = new Date()): Atraso {
  const tarde = works.filter((w) => estaAtrasado(w.billing_year, w.billing_month, hoy));
  if (tarde.length === 0) return SIN_ATRASO;

  // Un solo trabajo sin precio vuelve `null` al total. Mostrar la suma de los
  // demás como si fuera el total sería decir un número que no es cierto.
  const hayPendiente = tarde.some((w) => w.total_cents === null);

  let masViejo = { year: tarde[0]!.billing_year, month: tarde[0]!.billing_month };
  for (const w of tarde) {
    if (
      comoNumero(w.billing_year, w.billing_month) < comoNumero(masViejo.year, masViejo.month)
    ) {
      masViejo = { year: w.billing_year, month: w.billing_month };
    }
  }

  return {
    cantidad: tarde.length,
    total_cents: hayPendiente ? null : tarde.reduce((a, w) => a + (w.total_cents ?? 0), 0),
    masViejo,
    clientes: new Set(tarde.map((w) => w.client_id)).size,
  };
}
